import { useEffect, useState } from 'react'
import { ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { fetchOtuScopeInfo } from '../../../Repository/Visualization/OtuCubes.js'
import type { OtuScopeInfo } from '../../../Generated/OtuScopeInfo.ts'
import { FLAG_METADATA } from '../OrderManagementPage/Modals/PipelineInformation/otuFlags.js'
import { reportInfoRows, resultInfoStripText, versionInfoRows, type ResultSettings } from './OtuResultInfo.logic.js'
import { flaggedSampleLabel, flaggedUnassignedSamples, unassignedReadsWarning } from '../../../Model/Order/unassignedReads.js'

/** The scope's result info, fetched once per scope; null while loading or after a failure. */
export function useOtuScopeInfo(scopeId: string | undefined): OtuScopeInfo | null {
  const [info, setInfo] = useState<OtuScopeInfo | null>(null)
  useEffect(() => {
    setInfo(null)
    if (!scopeId) return
    let current = true
    fetchOtuScopeInfo(scopeId)
      .then((received) => {
        if (current) setInfo(received)
      })
      .catch((e: unknown) => console.error('OTU result info could not be loaded', e))
    return () => {
      current = false
    }
  }, [scopeId])
  return info
}

const VERSION_COLUMNS = ['Database', 'Version / Stable Key', 'Run ID', 'Ingested At', 'Fingerprint', 'Git ID']

/** The XLSX's Version Info and Result Info sheets: one block per order, the report's settings once. */
export function OtuResultInfoDetails({ info, settings }: { info: OtuScopeInfo; settings: ResultSettings }) {
  const orders = info.orders ?? []
  const multi = orders.length > 1
  return (
    <div className="flex flex-col gap-4 text-sm">
      <section>
        <h3 className="font-semibold mb-1">Version Info</h3>
        {info.databaseVersionsMixed && (
          <p data-testid="otu-result-info-mixed" className="text-warning mb-1">
            The orders were identified against different database versions.
          </p>
        )}
        <table data-testid="otu-result-info-versions" className="table table-xs">
          <thead>
            <tr>
              {VERSION_COLUMNS.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const rows = versionInfoRows(order)
              return [
                multi && (
                  <tr key={`${order.orderId}-head`} className="bg-base-200">
                    <td colSpan={VERSION_COLUMNS.length} className="font-semibold">
                      Order: {order.orderName ?? order.orderId}
                    </td>
                  </tr>
                ),
                rows.length === 0 && (
                  <tr key={`${order.orderId}-none`}>
                    <td colSpan={VERSION_COLUMNS.length} className="italic">
                      No version metadata (legacy order)
                    </td>
                  </tr>
                ),
                ...rows.map((row) => (
                  <tr key={`${order.orderId}-${row.database}`}>
                    <td>{row.database}</td>
                    <td>{row.stableKey}</td>
                    <td>{row.runId}</td>
                    <td>{row.ingestedAt}</td>
                    <td className="max-w-48 truncate" title={row.fingerprint}>
                      {row.fingerprint}
                    </td>
                    <td>{row.gitId}</td>
                  </tr>
                ))
              ]
            })}
          </tbody>
        </table>
      </section>
      <UnassignedSamplesSection info={info} />
      <section>
        <h3 className="font-semibold mb-1">Result Info</h3>
        <table data-testid="otu-result-info-results" className="table table-xs">
          <tbody>
            <tr className="bg-base-200">
              <td colSpan={2} className="font-semibold">
                Report
              </td>
            </tr>
            {reportInfoRows(info, settings).map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.value}</td>
              </tr>
            ))}
            {orders.map((order) => {
              const flags = Object.entries(order.flags ?? {})
              const pipeline = order.gitCommitId ? [['Pipeline Commit', order.gitCommitId] as const] : []
              if (flags.length === 0 && pipeline.length === 0) return null
              return [
                <tr key={`${order.orderId}-head`} className="bg-base-200">
                  <td colSpan={2} className="font-semibold">
                    Order: {order.orderName ?? order.orderId}
                  </td>
                </tr>,
                ...pipeline.map(([label, value]) => (
                  <tr key={`${order.orderId}-${label}`}>
                    <td>{label}</td>
                    <td>{value}</td>
                  </tr>
                )),
                ...flags.map(([key, value]) => {
                  const meta = FLAG_METADATA[key]
                  return (
                    <tr key={`${order.orderId}-${key}`}>
                      <td title={meta?.description}>{meta?.label ?? key}</td>
                      <td>{meta?.format ? meta.format(value) : value}</td>
                    </tr>
                  )
                })
              ]
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}

/** The samples whose reads are largely without a valid BLAST hit, when there are any. */
function UnassignedSamplesSection({ info }: { info: OtuScopeInfo }) {
  const flagged = flaggedUnassignedSamples(info.unassigned?.samples)
  if (flagged.length === 0) return null
  return (
    <section data-testid="otu-result-info-unassigned-samples">
      <h3 className="font-semibold mb-1">Samples With Many Reads Without a Valid BLAST Hit</h3>
      <ul className="list-disc pl-5">
        {flagged.map((s) => (
          <li key={s.sampleId}>{flaggedSampleLabel(s)}</li>
        ))}
      </ul>
    </section>
  )
}

/** The scope's warning on reads without a valid BLAST hit, or null. */
function UnassignedReadsWarning({ info }: { info: OtuScopeInfo }) {
  const unassigned = info.unassigned
  const warning = unassigned && unassignedReadsWarning(unassigned.rawReads, unassigned.assignedRawReads, unassigned.samples)
  if (!warning) return null
  return (
    <span
      data-testid="otu-result-info-unassigned-warning"
      className="flex items-center gap-1 text-warning"
      title={flaggedUnassignedSamples(unassigned.samples).map(flaggedSampleLabel).join('\n')}
    >
      <ExclamationTriangleIcon className="h-4 w-4" />
      {warning}
    </span>
  )
}

/**
 * One line under the OTU table's header naming what the table was computed from, with the full sheets on demand, and
 * the table's [note] on what it leaves out.
 */
export function OtuResultInfoStrip({
  scopeId,
  settings,
  note
}: {
  scopeId: string | undefined
  settings: ResultSettings
  note?: string | null
}) {
  const info = useOtuScopeInfo(scopeId)
  const [open, setOpen] = useState(false)
  const noteText = note ? <span data-testid="otu-result-info-note">{note}</span> : null
  if (!info || (info.orders ?? []).length === 0) {
    return noteText && <div className="my-1 flex items-center gap-1 text-xs text-base-content/70">{noteText}</div>
  }
  const text = resultInfoStripText(info)
  return (
    <div className="relative my-1 flex items-center gap-1 text-xs text-base-content/70">
      <span data-testid="otu-result-info-strip">{text || 'No version metadata'}</span>
      <UnassignedReadsWarning info={info} />
      {noteText && <span aria-hidden="true">·</span>}
      {noteText}
      <button
        data-testid="otu-result-info-toggle"
        className="btn btn-ghost btn-xs btn-circle"
        aria-label="Version and result info"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-[70vh] max-w-[90vw] overflow-auto rounded-lg bg-white p-3 text-base-content shadow-lg">
          <button className="btn btn-ghost btn-xs btn-circle absolute right-2 top-2" aria-label="Close" onClick={() => setOpen(false)}>
            <XMarkIcon className="h-4 w-4" />
          </button>
          <OtuResultInfoDetails info={info} settings={settings} />
        </div>
      )}
    </div>
  )
}
